import { DRAFT_LEAGUE_AVG } from "../constants/leagueAverages";

const num = (v, d = null) => (typeof v === "number" && !Number.isNaN(v) ? v : d);
const round1 = (v) => (typeof v === "number" ? Number(v.toFixed(1)) : v);
const diffVs = (player, league) => Number((player - league).toFixed(1));

// PITCHERS
export function buildPitcherCard(p) {
  const fbBase = num(p.fbVelo, 0);
  const fbRecent = num(p.fbVeloRecent, null);
  const whiffBase = num(p.whiffPct, 0);
  const whiffRecent = num(p.whiffPctRecent, null);

  const fbDelta = fbRecent == null ? null : round1(fbRecent - fbBase);
  const whiffDelta = whiffRecent == null ? null : round1(whiffRecent - whiffBase);

  const tiles = [
    {
      key: "fb",
      label: "FB Velo",
      value: `${round1(p.fbVelo)} mph`,
      vs: diffVs(p.fbVelo, DRAFT_LEAGUE_AVG.fbVelo),
      unit: " mph"
    },
    {
      key: "whiff",
      label: "Whiff%",
      value: `${round1(p.whiffPct)}%`,
      vs: diffVs(p.whiffPct, DRAFT_LEAGUE_AVG.whiffPct),
      unit: " pp"
    },
    {
      key: "strike",
      label: "Strike%",
      value: `${round1(p.strikePct)}%`,
      vs: diffVs(p.strikePct, DRAFT_LEAGUE_AVG.strikePct),
      unit: " pp"
    },
  ];

  // Choose signature: prioritize Whiff% if >= +3 pp vs Lg, else FB Velo if >= +2 mph vs Lg, else Strike%
  const whiffTile = tiles.find(t => t.key === "whiff");
  const veloTile = tiles.find(t => t.key === "fb");
  const strikeTile = tiles.find(t => t.key === "strike");
  let signatureKey = "strike";
  if (whiffTile.vs >= 3) signatureKey = "whiff";
  else if (veloTile.vs >= 2) signatureKey = "fb";
  tiles.forEach(t => (t.signature = t.key === signatureKey));

  return {
    name: p.name,
    identityTag: p.identityTag || "Power FB + Developing SL",
    tierLabel: p.tierLabel || "Impact Arm",
    strengths: (p.strengths || ["Elite fastball velocity", "Strong fastball ride", "Quality slider velocity"]).slice(0, 3),
    improvements: (p.improvements || ["Command refinement", "Third pitch development"]).slice(0, 3),
    goalsShort: p.goalsShort || ["Raise first-pitch strike% by 5%", "Refine SL command in-zone"],
    goalsLong: p.goalsLong || ["Improve CH tunneling off FB", "Sustain whiff gains vs RHH/LHH"],
    developmentTiles: p.developmentTiles, // Preserve development tiles if they exist
    tiles: p.developmentTiles ? undefined : tiles, // Only use old tiles if no development tiles
    trendBadges: [
      { label: "FB Velo", delta: fbDelta, unit: " mph" },
      { label: "Whiff%", delta: whiffDelta, unit: " pp" }, // percentage points
    ]
  };
}

// HITTERS
export function buildHitterCard(h) {
  const hhBase = num(h.hardHitPct, 0);
  const hhRecent = num(h.hardHitPctRecent, null);
  const chaseBase = num(h.chasePct, 0);
  const chaseRecent = num(h.chasePctRecent, null);

  // For chase: lower is better; keep delta math normal—badge just displays the number.
  const hhDelta = hhRecent == null ? null : round1(hhRecent - hhBase);
  const chaseDelta = chaseRecent == null ? null : round1(chaseRecent - chaseBase) * -1; // positive = improvement

  const tiles = [
    {
      key: "avg",
      label: "AVG",
      value: h.avg.toFixed(3),
      vs: Number((h.avg - DRAFT_LEAGUE_AVG.avg).toFixed(3)),
      unit: ""
    },
    {
      key: "hardhit",
      label: "Hard-Hit%",
      value: `${round1(h.hardHitPct)}%`,
      vs: diffVs(h.hardHitPct, DRAFT_LEAGUE_AVG.hardHitPct),
      unit: " pp"
    },
    {
      key: "chase",
      label: "Chase%",
      value: `${round1(h.chasePct)}%`,
      // lower is better → invert sign so positive = better vs Lg
      vs: diffVs(DRAFT_LEAGUE_AVG.chasePct - h.chasePct, 0),
      unit: " pp"
    },
  ];

  // Choose signature: prioritize Hard-Hit% if >= +3 pp vs Lg, else Chase% advantage >= +2 pp, else AVG
  const hh = tiles.find(t => t.key === "hardhit");
  const ch = tiles.find(t => t.key === "chase");
  let signatureKey = "avg";
  if (hh.vs >= 3) signatureKey = "hardhit";
  else if (ch.vs >= 2) signatureKey = "chase";
  tiles.forEach(t => (t.signature = t.key === signatureKey));

  return {
    name: h.name,
    identityTag: h.identityTag || "Elite contact with developing power",
    tierLabel: h.tierLabel || "Run Producer",
    strengths: (h.strengths || ["Consistent hard contact", "Selective approach"]).slice(0, 3),
    improvements: (h.improvements || ["Situational hitting vs spin"]).slice(0, 3),
    goalsShort: h.goalsShort || ["Lower chase% by 3%", "Lift rate improvement middle/up"],
    goalsLong: h.goalsLong || ["Gap-to-gap power growth", "Stable zone contact > 80%"],
    developmentTiles: h.developmentTiles, // Preserve development tiles if they exist
    tiles: h.developmentTiles ? undefined : tiles, // Only use old tiles if no development tiles
    trendBadges: [
      { label: "Hard-Hit%", delta: hhDelta, unit: " pp" },
      { label: "Chase% (↓ good)", delta: chaseDelta, unit: " pp" },
    ]
  };
}

// selectors for top 3 using existing mock fields
export const top3Pitchers = (arr) =>
  [...arr].sort((a,b)=> (b.whiffPct ?? 0) - (a.whiffPct ?? 0)).slice(0,3).map(buildPitcherCard);

export const top3Hitters = (arr) =>
  [...arr].sort((a,b)=> (b.hardHitPct ?? 0) - (a.hardHitPct ?? 0)).slice(0,3).map(buildHitterCard);
